export default {
  admin: {
    pageTitle: "Administration",
    pageSubtitle: "Manage the role model: roles, permissions and users.",
    badge: "Administrator",
    unnamed: "administrator",
    sidebarAria: "Administration sidebar",
    navAria: "Administration sections",
    associationsNavGroupLabel: "Associations and unions",
    usersAndRolesNavGroupLabel: "Users and roles",
    creativeSelfDefinitionNavGroupLabel: "Creative self-definition",
    logsNavGroupLabel: "Logs and audit",
    logout: "Sign out",
    loadError: "Failed to load data. Please try later.",
    saving: "Saving…",
    save: "Save",
    edit: "Edit",
    delete: "Delete",
    cancel: "Cancel",
    found: "Found: {{count}}",
    shownOf: "Showing {{shown}} of {{total}}",
    nav: {
      overview: "Overview",
      users: "Users",
      roles: "Roles",
      creativeRolesCatalog: "Roles catalog",
      creativeRolesModeration: "New roles moderation",
      groupKinds: "Group kinds",
      groupsTree: "Associations tree",
      unverifiedMemberships: "Pending memberships",
      events: "Events",
      applicationLogs: "Application logs",
      businessAudit: "Business audit",
      auditEvents: "Event audit",
      auditDeposits: "Deposit audit",
      auditAssociationMemberships: "Union membership audit",
    },
    sectionSubtitle: {
      users: "Manage accounts and their roles",
      roles: "Manage roles and access permissions",
      creativeRolesCatalog: "Manage the catalog of self-definition roles",
      creativeRolesModeration: "Moderate suggested catalog roles for creative self-definition",
      groupKinds: "Directory of group kinds (Union, Community…)",
      groupsTree: "Hierarchy of industries, unions, and branches",
      unverifiedMemberships: "Pending membership requests awaiting approval",
      events: "Registry of all events with filters and Excel export",
      applicationLogs: "Read the tail of the daily Serilog file for this API instance",
      businessAudit: "Status and action history by entity id (deposit, event, registration, membership)",
      auditEvents: "Status and action history by event id (including related registrations)",
      auditDeposits:
        "Find a deposit by GUID, certificate, authors, or rights holders; status history for the selected record",
      auditAssociationMemberships: "Status and action history by union membership id",
    },
    creativeSelfDefinition: {
      suggestionsSectionTitle: "Suggested catalog roles",
      loadError: "Could not load suggestions",
      noAccess: "You do not have permission to moderate self-definition",
      actionError: "Action failed",
      refresh: "Refresh list",
      empty: "No role suggestions",
      colAuthor: "Author",
      colDirection: "Direction",
      colSub: "Sub-branch",
      colProposal: "Proposal",
      colProposedDescription: "Description in suggestion",
      colStatus: "Status",
      colActions: "Actions",
      approve: "Approve",
      approveTitle: "Approve role suggestion",
      approveCatalogDescription: "Catalog role description (optional)",
      approveCatalogDescriptionPlaceholder:
        "If empty, the server may use the suggestion text or its default handling.",
      approveConfirm: "Approve",
      reject: "Reject",
      rejectTitle: "Reject suggestion",
      rejectComment: "Moderator comment (optional)",
      rejectConfirm: "Reject",
      status: {
        pending: "Pending",
        approved: "Approved",
        rejected: "Rejected",
      },
      catalog: {
        title: "Catalog roles",
        lead: "Create roles under sub-branches, edit descriptions, or soft-delete (hide from the catalog).",
        refresh: "Refresh list",
        loadError: "Could not load taxonomy",
        actionError: "Action failed",
        empty: "No catalog roles in taxonomy yet",
        colName: "Role",
        colDescription: "Description",
        create: "Add role",
        createTitle: "New catalog role",
        createSubmit: "Create",
        createValidation: "Choose a sub-branch and enter a role name",
        subDirectionsMultiLabel: "Sub-branches (one or more)",
        subDirectionsHint: "The new catalog role will appear under each selected sub-branch.",
        createSubDirectionsRequired: "Select at least one sub-branch.",
        createNameRequired: "Enter a role name.",
        emptySubDirections: "There are no sub-branches for this direction in the taxonomy.",
        nameLabel: "Name",
        descriptionLabel: "Description",
        maxChars: "At most {{max}} characters.",
        editTitle: "Edit role description",
        deleteTitle: "Remove catalog role?",
        deleteLead: "The role will be hidden from the catalog (soft delete). Users cannot pick it for new profiles.",
        deleteConfirm: "Remove from catalog",
      },
    },
    applicationLogs: {
      disclaimerTitle: "Before you read logs",
      disclaimerK8s:
        "In Kubernetes or with multiple replicas, each request may hit a different pod. You only see logs from the instance that served this request. For a cluster-wide view, use stdout aggregation (Loki, ELK, etc.).",
      disclaimerPii:
        "Logs may contain personal data or secrets. The Application logs permission is granted only to a small trusted group (configured in permission management).",
      disclaimerRotation:
        "Only rotated daily files (log-YYYYMMDD.txt) are listed. A legacy non-rotated logs.txt file is not included.",
      emptyDates: "No daily log files are available (or the log directory is empty).",
      dateLabel: "Log date",
      maxBytesLabel: "Max bytes from end",
      maxLinesLabel: "Max lines from end",
      refresh: "Refresh",
      loadingLog: "Loading log…",
      emptyLog: "(empty tail for these settings)",
      loadDatesError: "Could not load the list of log dates.",
      loadLogError: "Could not load the log. Try again or adjust parameters.",
      validation: {
        bytesRequired: "Enter a whole number for max bytes from end.",
        bytesRange: "Max bytes from end must be between {{min}} and {{max}}.",
        linesRequired: "Enter a whole number for max lines from end.",
        linesRange: "Max lines from end must be between {{min}} and {{max}}.",
      },
      errors: {
        fileNotFound:
          "There is no rotated log file for this date on this instance. Pick another date or try another replica.",
        invalidReadParameters: "Read parameters are out of the allowed range.",
      },
    },
    businessAudit: {
      intro:
        "Domain audit for global LeninkaArt.Audit.* permissions. Enter the entity UUID and pick a type. Owners and moderators without a global audit permission can open history from the relevant screens.",
      entityKindLabel: "Entity type",
      entityIdLabel: "Entity id (GUID)",
      entityIdPlaceholder: "00000000-0000-0000-0000-000000000000",
      eventSearchLabel: "Event",
      eventSearchPlaceholder: "Start typing a title…",
      eventSearchClearAria: "Clear field",
      openEvent: "Open event",
      openEventAria: "Open event “{{title}}” in a new tab",
      eventSearchError: "Could not search events. Please try later.",
      depositSearchLabel: "Find deposit",
      depositSearchPlaceholder: "GUID, certificate number, author, or rights holder…",
      depositSearchClearAria: "Clear search field",
      depositSearchError: "Search failed. Please try later.",
      depositSearchResultsHeading: "Search results",
      depositSearchEmptyFilter:
        "Enter a query to find a deposit (an empty query does not load the full catalog).",
      depositSearchNoResults: "No matching deposits.",
      depositSearchColTitle: "Title",
      depositSearchColCertificate: "Certificate number",
      depositSearchColStatus: "Status",
      depositSearchColSubmittedAt: "Submitted",
      depositSearchPaginationAria: "Search results pagination",
      depositSearchRowOpenAria: "Open deposit audit: {{title}}",
      load: "Load",
      loading: "Loading audit entries…",
      found: "Total entries: {{count}}",
      empty: "No entries.",
      colWhen: "When",
      colEntityType: "Object type",
      colParticipant: "Participant",
      colActor: "Who",
      colAction: "Action",
      colStatus: "Status",
      colReason: "Reason / comment",
      paginationAria: "Audit pagination",
      validation: {
        invalidUuid: "Enter a valid GUID (UUID format).",
        noPermissionForKind: "You do not have permission for this entity type.",
      },
      errors: {
        forbidden: "You do not have permission to view audit for this entity.",
        notFound: "Entity was not found or is not available.",
        generic: "Could not load audit. Please try again later.",
      },
      kind: {
        deposit: "Deposit",
        event: "Event",
        eventRegistration: "Event registration",
        associationMembership: "Union membership",
      },
      entityTypeLabel: {
        Deposit: "Deposit",
        Event: "Event",
        EventRegistration: "Registration",
        AssociationMembership: "Membership",
      },
      sortByTimeLabel: "Sort by time",
      sortNewestFirst: "Newest first",
      sortOldestFirst: "Oldest first",
      filtersSection: "Filters",
      filters: {
        entityType: "Type",
        allEntityTypes: "All",
        participant: "Participant (name/login)",
        actor: "Who (name/login)",
        fromOccurredAt: "From (date and time)",
        toOccurredAt: "To (date and time)",
        datePlaceholder: "Select date",
        timePlaceholder: "Time",
        calendarFromAria: "Calendar: from date and time",
        calendarToAria: "Calendar: to date and time",
        timeFromAria: "Time: from",
        timeToAria: "Time: to",
        expand: "Show",
        collapse: "Hide",
        apply: "Apply",
        reset: "Reset",
      },
    },
    overview: {
      welcome: "Welcome, {{name}}",
      welcomeHint:
        "Portal admin panel. The sections below are available based on your access permissions.",
      servicesTitle: "Administration sections",
      sectionHintUsersRoles: "Accounts, roles and access permissions",
      sectionHintAssociations: "Group kinds, associations tree and pending memberships",
      sectionHintCreativeSelfDefinition: "Moderate proposed catalog roles for self-definition",
      sectionHintLogs: "Application logs, event registry and business audit",
    },
    events: {
      filterSearch: "Search",
      filterSearchPlaceholder: "Title or description…",
      filterCreator: "Creator",
      filterCreatorPlaceholder: "Creator name or username…",
      filterStatus: "Status",
      statusAll: "All statuses",
      filterStartFrom: "Starts from",
      filterStartTo: "Starts until",
      datePlaceholder: "Select date",
      colTitle: "Title",
      colStatus: "Status",
      colStart: "Start date",
      colCreator: "Creator",
      colCity: "City",
      colActions: "Actions",
      preview: "Preview",
      previewAria: "Open preview: {{title}}",
      audit: "Audit",
      auditAria: "Action history: {{title}}",
      auditModalTitle: "Event audit",
      export: "Export to Excel",
      exportAria: "Export to Excel: {{title}}",
      empty: "No events found",
      loadError: "Could not load events",
      retry: "Refresh",
      paginationAria: "Events pagination",
    },
    roles: {
      add: "Add role",
      searchPlaceholder: "Search by role name…",
      paginationAria: "Roles pagination",
      colName: "Role name",
      colDefault: "Default",
      colPublic: "Public",
      colActions: "Actions",
      permissions: "Permissions",
      staticBadge: "System",
      empty: "No roles found",
      loadError: "Failed to load roles",
      saveError: "Failed to save the role",
      deleteError: "Failed to delete the role",
      deleteTitle: "Delete role?",
      deleteMessage: "Role \u00ab{{name}}\u00bb will be deleted. This cannot be undone.",
    },
    roleForm: {
      createTitle: "New role",
      editTitle: "Edit role",
      name: "Role name",
      namePlaceholder: "Enter a name",
      nameRequired: "Role name is required",
      isDefault: "Default role",
      isDefaultHint: "Automatically assigned to new users",
      isPublic: "Public role",
      isPublicHint: "Visible to other users",
    },
    users: {
      searchPlaceholder: "Search by email, name, surname, or phone…",
      paginationAria: "Users pagination",
      colEmail: "Email",
      colName: "Name",
      colSurname: "Surname",
      colPhone: "Phone",
      colStatus: "Status",
      colActions: "Actions",
      roles: "Roles",
      active: "Active",
      inactive: "Disabled",
      emailConfirmed: "Email confirmed",
      empty: "No users found",
      loadError: "Failed to load users",
      deleteError: "Failed to delete the user",
      deleteTitle: "Delete user?",
      deleteMessage: "User {{email}} will be deleted. This cannot be undone.",
    },
    userRoles: {
      title: "User roles",
      searchPlaceholder: "Search by role name…",
      empty: "No roles found",
      selectedBadge: "Selected",
      loadError: "Failed to load user roles",
      saveError: "Failed to save user roles",
    },
    permissions: {
      title: "Access permissions",
      subtitle: "Role: {{role}}",
      empty: "No permissions available for this role",
      loadError: "Failed to load permissions",
      saveError: "Failed to save permissions",
      expand: "Expand",
      collapse: "Collapse",
    },
    associations: {
      loadError: "Failed to load data",
      saveError: "Failed to save",
      deleteError: "Failed to delete",
      noSelection: "Select a node in the tree",
      rootSelector: "Group kind",
      rootSelectorPlaceholder: "Select a kind",
      groupKinds: {
        add: "Add kind",
        colName: "Name",
        colCode: "Code",
        colOrder: "Order",
        colIcon: "Icon",
        colActions: "Actions",
        empty: "No group kinds defined yet",
        createTitle: "New group kind",
        editTitle: "Edit group kind",
        code: "Code (lowercase)",
        codeHint: "Lowercase letters, digits, dashes. e.g. union, community-local.",
        codeInvalid: "Only lowercase latin letters, digits, and dashes (up to 64 chars).",
        codeTaken: "This code is already taken.",
        name: "Name",
        order: "Order",
        iconUrl: "Icon URL",
        deleteTitle: "Delete group kind?",
        deleteMessage:
          "\u00ab{{name}}\u00bb will be deleted. Make sure no groups reference this kind.",
        deleteInUse:
          "Cannot delete: there are groups referencing this kind. Move or delete them first.",
      },
      tree: {
        addRoot: "New root group",
        addSubgroup: "New subgroup",
        rename: "Edit",
        move: "Move",
        remove: "Delete",
        empty: "No groups for the selected kind",
        inactive: "Inactive",
        autoAccept: "Auto-accept",
        noSelfServiceJoin: "No self-service join",
        moderators: "Moderators: {{count}}",
        deleteTitle: "Delete group?",
        deleteMessage:
          "\u00ab{{name}}\u00bb will be deleted. This cannot be undone.",
        hasChildren: "Group has child nodes. Delete or move them first.",
        hasMembers: "Group has active members. Deletion is forbidden.",
        moveTitle: "Move group",
        moveMessage: "Select a new parent of the same kind.",
        makeRoot: "Make root",
        moveSubmit: "Move",
        moveInvalid: "New parent must be of the same kind and not a descendant.",
        createRootTitle: "Create root group",
        createSubgroupTitle: "Create subgroup",
        editTitle: "Edit group",
        name: "Name",
        order: "Order",
        description: "Description",
        iconUrl: "Icon URL",
        externalUrl: "External URL",
        requiresApproval: "Applications require approval",
        requiresApprovalHint:
          "If disabled, requests are auto-approved on submission.",
        isActive: "Group is active",
        isActiveHint:
          "If disabled, new memberships are rejected, existing ones are kept.",
        allowModeratorSubgroups: "Moderators can create subgroups",
        allowModeratorSubgroupsHint:
          "Editable only by an associations administrator.",
        allowsUserMembership: "Users may join this group",
        allowsUserMembershipHint:
          "If disabled, users cannot submit a membership request for this group.",
        save: "Save",
        create: "Create",
        cancel: "Cancel",
      },
      moderators: {
        title: "Moderators",
        colUser: "User",
        colEmail: "E-mail",
        colActions: "Actions",
        empty: "No moderators assigned",
        addTitle: "Assign moderator",
        addSubmit: "Assign",
        searchPlaceholder: "Search by name or email…",
        remove: "Remove",
        removeTitle: "Remove moderator?",
        removeMessage: "{{name}} will no longer moderate this branch.",
        alreadyAssigned: "This user is already a moderator here.",
        cannotRemoveLastRoot:
          "Cannot remove the last root moderator without the \u00abAssociations.Manage\u00bb permission.",
        notModerator: "Action is not allowed outside of your subtree.",
      },
      specializations: {
        title: "Industry specializations",
        subtitle: "Available only for root groups (industries).",
        add: "Add specialization",
        empty: "No specializations yet",
        name: "Name",
        order: "Order",
        save: "Save",
        cancel: "Cancel",
        deleteTitle: "Delete specialization?",
        deleteMessage: "\u00ab{{name}}\u00bb will be deleted.",
        industryMismatch:
          "Specialization must belong to the same industry as the selected node.",
      },
      unverified: {
        colUser: "User",
        colGroup: "Path",
        colSpecialization: "Specialization",
        colSubmittedOn: "Submitted on",
        colActions: "Actions",
        verify: "Approve",
        reject: "Reject",
        empty: "No pending requests",
        loadError: "Failed to load pending requests",
        noAccess: "You do not have access to the queue.",
        alreadyApproved: "The request is already approved.",
        notModerator: "You are not a moderator of this subtree.",
        membershipNotAllowed:
          "User membership is disabled for this group — approval is not possible.",
        rejectTitle: "Reject request",
        rejectLead: "The request will be marked rejected. The applicant will see the reason you provide.",
        rejectReasonLabel: "Rejection reason",
        rejectReasonPlaceholder: "Briefly explain why…",
        rejectReasonRequired: "A rejection reason is required.",
        rejectReasonMax: "At most 1024 characters.",
        rejectError: "Could not reject the request.",
      },
    },
  },
}
